import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";

import {
  apiCreateMachineGroup,
  apiUpdateMachineGroup,
  apiDeleteMachineGroup, apiUpdateMachineGroupImages,
} from "api";
import { selectToken } from "slices/tokenSlice";
import {
  startOngoing,
  endOngoing,
  batchSuccess,
  batchFailure,
} from "slices/shared";

const adapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const machineGroups = createSlice({
  name: "machineGroups",
  initialState: adapter.getInitialState({
    ongoing: {},
  }),

  reducers: {
    start: startOngoing,
    end: endOngoing,
    set(state, { payload }) {
      adapter.setAll(state, payload);
    },
    add(state, { payload }) {
      adapter.addOne(state, payload);
    },
    update(state, { payload }) {
      adapter.updateOne(state, {
        id: payload.id,
        changes: payload,
      });
    },
    remove(state, { payload }) {
      adapter.removeOne(state, payload);
    },
    upsertMany(state, { payload }) {
      adapter.upsertMany(state, payload);
    },
    removeMany(state, { payload }) {
      adapter.removeMany(state, payload);
    },
  },
});

export default machineGroups.reducer;

const {
  start,
  end,
  set,
  add,
  update,
  remove,
  upsertMany,
  removeMany,
} = machineGroups.actions;

export { set, upsertMany, removeMany };

const selectState = (state) => state.machineGroups;

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = adapter.getSelectors(selectState);

export const selectOngoing = (state, key) => !!selectState(state).ongoing[key];

export const addMachineGroup = (data, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(start("addMachineGroup"));
    const machineGroup = await apiCreateMachineGroup(
      selectToken(getState()),
      data
    );
    batchSuccess(dispatch, options, add(machineGroup), end("addMachineGroup"));
    return machineGroup;
  } catch (error) {
    batchFailure(dispatch, error, options, end("addMachineGroup"));
    throw error;
  }
};

export const updateMachineGroup = (id, data, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(start("updateMachineGroup"));
    const machineGroup = await apiUpdateMachineGroup(
      selectToken(getState()),
      id,
      data
    );
    batchSuccess(
      dispatch,
      options,
      update(machineGroup),
      end("updateMachineGroup")
    );
  } catch (error) {
    batchFailure(dispatch, error, options, end("updateMachineGroup"));
    throw error;
  }
};

export const updateMachineGroupImages = (
  id,
  data,
  options
) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(start("updateMachineGroupImages"));
    const machineGroup = await apiUpdateMachineGroupImages(
      selectToken(getState()),
      id,
      data
    );
    batchSuccess(
      dispatch,
      options,
      update(machineGroup),
      end("updateMachineGroupImages")
    );
  } catch (error) {
    batchFailure(dispatch, error, options, end("updateMachineGroupImages"));
    throw error;
  }
};

export const removeMachineGroup = (id, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(start("removeMachineGroup"));
    await apiDeleteMachineGroup(selectToken(getState()), id);
    batchSuccess(dispatch, options, remove(id), end("removeMachineGroup"));
  } catch (error) {
    batchFailure(dispatch, error, options, end("removeMachineGroup"));
    throw error;
  }
};
